/**
 * Shared agent color pool — single source of truth for agent avatar colors.
 *
 * Used for the color picker in agent settings and as a fallback when
 * a template has no persisted color.
 */
export const AGENT_COLORS = [
  "#30D158",
  "#60A5FA",
  "#A78BFA",
  "#F59E0B",
  "#EF4444",
  "#EC4899",
  "#14B8A6",
  "#6366F1",
  "#F97316",
  "#06B6D4",
] as const;

/** Fallback: pick a color by index (for backwards compat before migration). */
export function agentColorByIndex(index: number): string {
  return AGENT_COLORS[index % AGENT_COLORS.length]!;
}

const DENKER_COLOR = "#30D158";

function hashAgentIdentity(identity: string): number {
  let hash = 2166136261;
  for (let index = 0; index < identity.length; index += 1) {
    hash ^= identity.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function hslToHex(hue: number, saturation: number, lightness: number): string {
  const s = saturation / 100;
  const l = lightness / 100;
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const segment = hue / 60;
  const x = chroma * (1 - Math.abs((segment % 2) - 1));
  const [red, green, blue] =
    segment < 1
      ? [chroma, x, 0]
      : segment < 2
        ? [x, chroma, 0]
        : segment < 3
          ? [0, chroma, x]
          : segment < 4
            ? [0, x, chroma]
            : segment < 5
              ? [x, 0, chroma]
              : [chroma, 0, x];
  const offset = l - chroma / 2;
  return `#${[red, green, blue]
    .map((channel) =>
      Math.round((channel + offset) * 255)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`.toUpperCase();
}

/**
 * Give native workers a stable visual identity without reviving persisted
 * agent-template colors. Denker owns the product green; worker hues are
 * derived from the runtime identity and avoid the adjacent green range.
 */
export function agentAccentColor(name: string, identity: string): string {
  const baseName = name.split(" · ")[0]?.trim().toLowerCase();
  if (baseName === "denker") return DENKER_COLOR;

  const hueWithoutGreen = hashAgentIdentity(identity || name) % 290;
  const hue = hueWithoutGreen >= 90 ? hueWithoutGreen + 70 : hueWithoutGreen;
  return hslToHex(hue, 72, 64);
}
